import React from "react"

import {
  getLenderApplicationsRetrieveQueryOptions,
  useLenderApplicationsRetrieveSuspense,
} from "@boundless/bl-api-client/lender/services"
import {
  createFileRoute,
  Link,
  notFound,
  redirect,
} from "@tanstack/react-router"
import { ChevronLeftIcon } from "lucide-react"

import { ApplicationStatusCard } from "@/components/applications/lender/ApplicationStatusCard"
import { FundingNeedsCard } from "@/components/applications/lender/FundingNeedsCard"
import { NoDocumentAccessCard } from "@/components/applications/lender/NoDocumentAccessCard"
import { BusinessDocumentsCard } from "@/components/documents/BusinessDocumentsCard"
import { CategorizedDocumentsCard } from "@/components/documents/CategorizedDocumentsCard"
import { LenderDocumentProvider } from "@/components/documents/context/DocumentsProvider"
import { PendingComponent } from "@/components/error/PendingComponent"
import { DownloadLinkButton } from "@/components/ui/button"
import { isNotFoundError } from "@/lib/clients"

export const Route = createFileRoute(
  "/_dashboard/lender/applications/details/$uuid",
)({
  component: RouteComponent,
  beforeLoad: ({ context: { hasPermissions } }) => {
    if (!hasPermissions(["applications.view_application"])) {
      throw redirect({ to: "/" })
    }
  },
  loader: async ({ context: { queryClient }, params: { uuid } }) => {
    const options = getLenderApplicationsRetrieveQueryOptions(uuid)
    try {
      await queryClient.fetchQuery(options)
    } catch (error: unknown) {
      if (isNotFoundError(error)) throw notFound()
      throw error
    }
  },
})

function RouteComponent() {
  const { uuid } = Route.useParams()

  const { data: application } = useLenderApplicationsRetrieveSuspense(uuid)

  return (
    <div className="flex flex-col gap-6">
      <Link
        to={"/lender/applications/list"}
        className="flex flex-row items-center"
      >
        <ChevronLeftIcon />
        Back
      </Link>

      <div className="flex flex-row items-center px-5">
        <h1 className="text-lg font-bold">
          {application.companyName} Application
        </h1>

        <DownloadLinkButton
          url={application.lenderExportUrl}
          className="ml-auto"
        >
          Export
        </DownloadLinkButton>
      </div>
      <FundingNeedsCard application={application} />
      {application.lenderHasRepositoryAccess ? (
        <React.Suspense fallback={<PendingComponent hideLogo />}>
          <LenderDocumentProvider applicationUuid={application.uuid}>
            <BusinessDocumentsCard />
            <CategorizedDocumentsCard />
          </LenderDocumentProvider>
        </React.Suspense>
      ) : (
        <NoDocumentAccessCard />
      )}
      <ApplicationStatusCard application={application} />
    </div>
  )
}
